import * as React from 'react';
import {view} from '@sail/ui';

export type ContentStateSize = 'small' | 'medium' | 'large';
export type ContentStateProps = {
  actions?: React.ReactNode;
  align?: 'start' | 'center';
  dataTestId?: string;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  padding?: string;
  size?: ContentStateSize;
  title?: React.ReactNode;
};

const getTitleFont = (size: ContentStateSize) => {
  switch (size) {
    case 'small':
      return 'body.small';
    case 'medium':
      return 'heading.small.subdued';
    default:
      return 'heading.medium';
  }
};

const getDescriptionFont = (size: ContentStateSize) => {
  switch (size) {
    case 'small':
      return 'label.small';
    case 'medium':
      return 'body.small';
    default:
      return 'body.medium';
  }
};

const getGapStyles = (size: ContentStateSize) => {
  switch (size) {
    case 'large':
      return 'space.300';
    case 'medium':
      return 'space.150';
    default:
      return 'space.100';
  }
};

export const ContentState = ({
  actions,
  align,
  dataTestId,
  description,
  icon,
  padding,
  size = 'medium',
  title,
}: ContentStateProps) => {
  const getAlignX = () => {
    if (align) {
      return align;
    }
    if (size === 'large') {
      return 'start';
    }
    return 'center';
  };
  const alignX = getAlignX();

  const shouldRenderActions = size !== 'small' && !!actions;
  const renderActions = () => {
    if (!shouldRenderActions) return null;
    return actions;
  };

  const shouldRenderDescription = size !== 'small' && !!description;
  const shouldRenderContent = !!title || shouldRenderDescription;
  const renderContent = () => {
    if (!shouldRenderContent) return null;
    return (
      <view.div
        css={{
          stack: 'y',
          alignX,
          gap: 'space.75',
          textAlign: alignX === 'start' ? 'left' : 'center',
        }}
      >
        {title && (
          <view.span
            css={{
              color: 'text',
              font: getTitleFont(size),
              fontWeight: size !== 'small' ? 'semibold' : 'regular',
            }}
          >
            {title}
          </view.span>
        )}
        {shouldRenderDescription && (
          <view.span
            css={{
              color: 'subdued',
              font: getDescriptionFont(size),
            }}
          >
            {description}
          </view.span>
        )}
      </view.div>
    );
  };

  return (
    <view.div
      css={{
        stack: 'x',
        alignY: 'center',
        alignX,
        padding: padding === undefined ? 'space.600' : padding,
      }}
      data-testid={dataTestId}
    >
      <view.div
        css={{
          stack: size === 'small' ? 'x' : 'y',
          gap: getGapStyles(size),
          alignX,
          alignY: 'baseline',
          width: 'fit',
        }}
      >
        {icon}

        {(shouldRenderActions || shouldRenderContent) && (
          <view.div
            css={{stack: 'y', gap: 'space.350', alignX}}
            data-testid="content-state-body"
          >
            {renderContent()}
            {renderActions()}
          </view.div>
        )}
      </view.div>
    </view.div>
  );
};

export default ContentState;
